<template>
  <b-dropdown
    :id="id"
    ref="dropdown"
    class="template-picker"
    :disabled="disabled"
    :dropup="dropup"
    :block="block"
    :variant="variant"
    :size="size"
    :toggle-attrs="{ 'data-cy': 'message-templates' }"
    lazy
    @shown="focusSearch"
    @hidden="reset"
  >
    <template #button-content>
      <slot name="button-content">{{ label }}</slot>
    </template>

    <b-dropdown-form
      class="template-picker-search"
      @submit.prevent="selectFirstMatch"
    >
      <b-form-input
        ref="search"
        v-model="query"
        size="sm"
        autocomplete="off"
        :placeholder="searchPlaceholder"
        data-cy="template-search"
      />
    </b-dropdown-form>

    <div class="template-picker-list">
      <template v-for="(template, index) in filteredTemplates">
        <b-dropdown-header
          v-if="startsGroup(index)"
          :key="`group-${template.group}`"
          data-cy="template-group"
        >
          {{ template.group }}
        </b-dropdown-header>
        <b-dropdown-item-button
          :key="keyFor(template, index)"
          :title="template.text"
          data-cy="template-option"
          @click="select(template)"
        >
          {{ template.text }}
        </b-dropdown-item-button>
      </template>

      <b-dropdown-text v-if="!filteredTemplates.length" class="template-empty">
        {{ emptyText || `No templates match “${query.trim()}”` }}
      </b-dropdown-text>
    </div>
  </b-dropdown>
</template>

<script>
import { matchesQuery } from '@/utils/autocompleteFilter'

/**
 * Searchable template list for the chat composer — one component, so the
 * account page and the inbox stay identical. Options arrive in the
 * `{ value, text }` shape Chat already builds from the message-templates
 * endpoint. The group compose modal wants a form control rather than a
 * toolbar dropdown, so it uses ModelSelect instead.
 */
export default {
  name: 'TemplatePicker',
  props: {
    templates: {
      type: Array,
      default: () => []
    },
    id: {
      type: String,
      default: 'template-picker'
    },
    label: {
      type: String,
      default: 'Select a template'
    },
    searchPlaceholder: {
      type: String,
      default: 'Search templates...'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    dropup: {
      type: Boolean,
      default: false
    },
    variant: {
      type: String,
      default: 'secondary'
    },
    size: {
      type: String,
      default: ''
    },
    // Full width, for a form field rather than a toolbar button.
    block: {
      type: Boolean,
      default: false
    },
    // Replaces "No templates match …" when the options are not templates.
    emptyText: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      query: ''
    }
  },
  computed: {
    filteredTemplates() {
      return this.templates.filter(template =>
        matchesQuery(this.query, template.text)
      )
    }
  },
  methods: {
    focusSearch() {
      this.$nextTick(() => {
        const input = this.$refs.search
        input && input.focus()
      })
    },
    reset() {
      this.query = ''
    },
    keyFor(template, index) {
      // Template names are not unique, so fall back to position rather than
      // letting two same-named templates share a key.
      const id = template.value && template.value.id

      return id || `${template.text}-${index}`
    },
    // An option's optional `group` heads the run of options that share it, so pass them in group order.
    startsGroup(index) {
      const { group } = this.filteredTemplates[index]

      return (
        Boolean(group) && this.filteredTemplates[index - 1]?.group !== group
      )
    },
    select(template) {
      this.$emit('select', template.value)
    },
    selectFirstMatch() {
      const [first] = this.filteredTemplates
      if (!first) return

      this.select(first)
      // Clicking an option closes the menu for free; submitting the search
      // form does not, so Enter has to close it to match.
      this.$refs.dropdown.hide()
    }
  }
}
</script>

<style lang="scss">
/**
 * The panel geometry is deliberately fixed. Left to size itself, the menu takes
 * the width of its longest visible option and the height of however many
 * options survive the filter — so every keystroke resized the panel in both
 * axes, and on a dropup the top edge slid down toward the cursor while the
 * reader was still looking at it. Constant width plus a constant list height
 * means typing only changes which rows are in the box, never the box.
 */
.template-picker {
  $picker-width: 20rem;
  $picker-list-height: 15rem;

  .dropdown-menu {
    width: $picker-width;
    min-width: $picker-width;
    max-width: calc(100vw - 2rem);
    padding: 0;
    overflow: hidden;
  }

  .template-picker-search {
    padding: 0.5rem;

    background: white;
    border-bottom: 1px solid $lighter-gray;
  }

  .template-picker-list {
    // Fixed, not max-height: a shrinking result set leaves empty space rather
    // than dragging the panel edge around.
    height: $picker-list-height;
    overflow-y: auto;
    // Reserve the scrollbar track so gaining or losing it never reflows rows.
    scrollbar-gutter: stable;
  }

  // Rows fill the panel edge to edge. Long names truncate instead of widening
  // the menu; the full name stays available as a title tooltip.
  .dropdown-item {
    overflow: hidden;
    padding: 0.5rem 0.75rem;

    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .template-empty {
    padding: 0.75rem;

    color: $steel-gray;
    font-style: italic;

    // b-dropdown-text wraps its content in a <p>, which arrives with the
    // reboot's bottom margin.
    p {
      margin: 0;
    }
  }
}
</style>
